import React, { useEffect, useReducer, useRef, useState } from "react";
import { showDemoDownloadModal } from "../../demo/demoModal";
import {
  Box,
  Button,
  getColor,
  Icons,
  Modal,
  Tooltip,
  Typography,
  useToast,
} from "fds";
import "./index.scss";
import ExistWorkgroupPage from "./ExistWorkgroupPage";
import CreateNewWorkgroupPage from "./CreateNewWorkgroupPage";
import { nanoid } from "@reduxjs/toolkit";
import SetPolicyPage from "../SePolicyPage";
import { DocInfo, PermissionPolicy, UploadFile } from "../../types/files";
import useWorkgroupStore from "../../redux/dispatcher/useWorkgroupStore";
import utilsCommon from "../../shared/utils/common";
import { getUserInfoFromLocalStorage } from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";
import {
  Member,
  Workgroup,
  WorkgroupCreateInfo,
  WorkgroupCreateMembers,
} from "../../types/workgroup";
import { useNavigate } from "react-router-dom";
import useDocumentStore from "../../redux/dispatcher/useDocumentStore";
import FileUploadModal from "../../pages/Workgroup/WorkgroupFile/Modal/FileUploadModal";
import { t } from "i18next";
import utilsClient from "../../shared/utils/client";
import SelectUserModal from "./SelectUserModal";
import dayjs from "dayjs";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import i18n from "../../locales/i18n";
interface WorkgroupAddModalProps {
  classes?: string;
  /**
   * SHARE_TO_WORKGROUP: 워크그룹 파일 공유 => 기존워크그룹 또는 새 워크그룹 생성 선택화면 이동
   * CREATE_NEW_WORKGROUP: 워크그룹 생성 => 바로 새 워크그룹 생성 화면으로
   * CREATE_NEW_SUB_WORKGROUP: 하위 워크그룹 생성 => 바로 하위 워크그룹 생성 화면으로
   * CREATE_NEW_MEETING: 화상 회의 예약 => 바로 새 워크그룹 생성 화면으로
   * SEND_FILE: 전송 파일 => 파일 업로드 부터 진행
   * ADD_RECIPIENTS_SEND_FILE: 전송파일 => 수신자 선택부터 진행
   * ADD_NEW_MEMBER: 멤버 추가
   */
  createType:
    | "SHARE_TO_WORKGROUP"
    | "CREATE_NEW_WORKGROUP"
    | "CREATE_NEW_SUB_WORKGROUP"
    | "CREATE_NEW_MEETING"
    | "SEND_FILE"
    | "ADD_RECIPIENTS_SEND_FILE"
    | "ADD_NEW_MEMBER";
  open: boolean;
  workgroupInfo?: Workgroup;
  disableValues?: string[];
  uploadedFiles?: DocInfo[]; // 이미 에코에 올라가있는 파일을 다른 워크그룹으로 옮길 경우
  receivedFiles?: UploadFile[];
  targetType?: "DROPBOX" | "client";
  clientUid?: string;
  messageInfo?: {
    sendShareMail: boolean;
    message: string;
  };
  onSubmitAfter?: (
    users?: {
      email?: string | null;
      status: number;
      userId: string;
      userName?: string | null;
      userType: number;
      policy?: PermissionPolicy;
    }[],
    messageInfo?: { sendShareMail: boolean; message: string }
  ) => void;
  parentWorkgroupId?: string | null; // 상위 워크그룹 ID
  onClose: () => void;
}

const WorkgroupAddModal: React.FC<WorkgroupAddModalProps> = ({
  classes,
  open,
  receivedFiles,
  uploadedFiles,
  createType,
  workgroupInfo,
  disableValues,
  clientUid,
  targetType,
  messageInfo,
  parentWorkgroupId,
  onClose,
  onSubmitAfter,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);
  const myId = loginDto?.userInfo?.userId ?? "";
  const INIT_WORKGROUP_INFO = {
    workgroupName: "",
    desc: "",
    managerId: myId,
    invitePolicy: 0,
    members: [] as WorkgroupCreateMembers[],
    startDate: -1,
    endDate: -1,
    status: 1,
    invitedMessage: "",
    parentWorkgroupId: workgroupInfo?.workgroupId,
    createWorkgroupHub: false,
  };

  const {
    workgroupState,
    getWorkgroupSideList,
    getWorkspaceConfig,
    createNewWorkgroup,
    createNewPrivateWorkgroups,
    updateWorkgroupInfo,
    getMemberList,
    getWorkgroupDetail,
  } = useWorkgroupStore();
  const {
    documentState,
    removeUid,
    getDocUploadUidCreate,
    getDocDeliveryUid,
    doDocUploadFileComplete,
    doDocUploadFileCompleteDelivery,
    setUpdateUploadProgressFiles,
  } = useDocumentStore();
  const { loginState } = useLoginStore();

  const navigate = useNavigate();
  const toasts = useToast();

  const getInitStep = () => {
    if (createType === "SHARE_TO_WORKGROUP")
      return uploadedFiles || targetType === "client"
        ? "CHOOSE_TYPE"
        : "FILE_UPLOAD";
    else if (createType === "ADD_NEW_MEMBER") return "ADD_NEW_MEMBER";
    else if (createType === "SEND_FILE")
      return targetType === "client" ? "SELECT_RECEIVER" : "FILE_UPLOAD";
    else if (createType === "ADD_RECIPIENTS_SEND_FILE")
      return "SELECT_RECEIVER";
    else if (createType == "CREATE_NEW_SUB_WORKGROUP")
      return "CREATE_NEW_SUB_WORKGROUP";
    else return "CREATE_NEW_WORKGROUP";
  };
  const date = new Date();
  const serverTimezone = -date.getTimezoneOffset() / 60;
  const isOCIDefaultSite =
    window.eco.serviceType === "OCI" &&
    utilsCommon.getWorkspaceCode() === "MTECOCOMMON00001";
  const customPolicy = createType.includes("WORKGROUP")
    ? utilsCommon.getValueFromKeyinEcoCommonConfig("ecoCommonConfig")
        ?.workgroupPermissionDTO?.policy ??
      loginDto?.ecoCommonConfig?.workgroupPermissionDTO?.policy ?? {
        startTime: dayjs().valueOf(),
        allowDays: -1,
        allowDaysTimeZone: serverTimezone,
        allowViewCount: 0,
        currentViewCount: 0,
        allowDownloadCount: 0,
        currentDownloadCount: 0,
        drmPerm: isOCIDefaultSite
          ? []
          : ["view", "print", "print_screen", "extract"],
        webView: true,
        revision: true,
        download: true,
        wrapping: !isOCIDefaultSite,
        additionalViewPermission: "NONE",
      }
    : loginDto?.ecoCommonConfig?.noneWorkgroupPolicyDTO?.policy ?? {
        startTime: dayjs().valueOf(),
        allowDays: -1,
        allowDaysTimeZone: serverTimezone,
        allowViewCount: 0,
        currentViewCount: 0,
        allowDownloadCount: 0,
        currentDownloadCount: 0,
        drmPerm: isOCIDefaultSite
          ? []
          : ["view", "print", "print_screen", "extract"],
        webView: true,
        revision: true,
        download: true,
        wrapping: !isOCIDefaultSite,
        additionalViewPermission: "NONE",
      };
  /**
   * CHOOSE_TYPE: 워크그룹 선택
   * UPLOAD_TO_EXIST_WORKGROUP: 기존 워크그룹으로 파일 공유
   * ADD_NEW_MEMBER: 멤버 추가
   * CREATE_NEW_WORKGROUP:새 워크그룹으로 파일 공유
   * FILE_UPLOAD: 파일 업로드창
   * SELECT_RECEIVER: 전송파일 멤버 선택
   * CREATE_PRIVATE_WORKGROUP: 비공개 워크그룹 생성 화면
   */
  const [currentStep, changeCurrentStep] = useState<
    | "CHOOSE_TYPE"
    | "CREATE_NEW_WORKGROUP"
    | "CREATE_NEW_SUB_WORKGROUP"
    | "UPLOAD_TO_EXIST_WORKGROUP"
    | "ADD_NEW_MEMBER"
    | "FILE_UPLOAD"
    | "SELECT_RECEIVER"
    | "CREATE_PRIVATE_WORKGROUP"
  >(getInitStep());
  // >("CREATE_PRIVATE_WORKGROUP");
  const [uid, changeUid] = useState(clientUid);
  const [files, changeFiles] = useState(receivedFiles);
  const [extraOpenModalInfo, changeExtraOpenModalInfo] = useState("");
  const [isNextButtonDisabled, changeIsNextButtonDisabled] = useState(true);
  const [newWorkgroupInfo, changeNewWorkgroupInfo] =
    useState<WorkgroupCreateInfo>(workgroupInfo ?? INIT_WORKGROUP_INFO);
  const [isSendShareMail, changeSendShareMail] = useState(
    messageInfo?.sendShareMail ??
      loginDto?.ecoCommonConfig?.noneWorkgroupPolicyDTO?.policy
        ?.sendShareMail ??
      true
  );
  const [policy, changePolicy] = useState<PermissionPolicy>({
    ...customPolicy,
    allowDays: customPolicy.allowDays - 1,
  });
  const [tempPolicy, changeTempPolicy] = useState<PermissionPolicy>({
    ...customPolicy,
    allowDays: customPolicy.allowDays - 1,
  });
  const [addedUserList, changeAddedUserList] = useState<
    | {
        email?: string | null;
        status: number;
        userId: string;
        userName?: string | null;
        userType: number;
      }[]
  >([]);
  const [users, changeUsers] = useState<string[]>([]);
  /** 문서 복사시 사용할 아이 */
  const [copyFileResp, changeCopyFileResp] = useState<{
    files: DocInfo[];
    workgroupId?: string;
    workgroupName?: string;
    message?: string;
  }>({
    files: uploadedFiles ?? [],
  });
  const [targetPermissionUserList, changeTargetPermissionUserList] = useState<
    string[]
  >([]);
  const [permissionList, changePermissionList] = useState<
    { targetValue: string; policy: PermissionPolicy }[]
  >([]);
  const [message, changeMessage] = useState(messageInfo?.message ?? "");
  const [extraSize, changeExtraSize] = useState(0);
  const [myBoxFileShareLinkPolicy, changeMyBoxFileShareLinkPolicy] = useState({
    authType: "LOGIN",
    urlAction: "WEBUSING_AND_DOWNLOAD",
    allowDays: -1,
  }); // My Box에서 사용하는 링크 관련 내역
  const modalHeight = useRef(0);

  useEffect(() => {
    getWorkspaceConfig();
    if (utilsCommon.isClientLogin("WINDOWS")) {
      window?.chrome?.webview?.postMessage("getShareData");
      window?.chrome?.webview?.addEventListener("message", (evt: any) => {
        changeUsers(evt.data.TargetAddresses);
      });
    }
    getWorkgroupSideList(workgroupState.savedSidemenuFilter);
    if (
      loginDto?.ecoCommonConfig?.noneWorkgroupPolicyDTO?.policy
        ?.fileShareLinkPolicy
    ) {
      changeMyBoxFileShareLinkPolicy({
        ...loginDto?.ecoCommonConfig?.noneWorkgroupPolicyDTO?.policy
          ?.fileShareLinkPolicy,
        allowDays:
          loginDto?.ecoCommonConfig?.noneWorkgroupPolicyDTO?.policy
            .fileShareLinkPolicy.allowDays - 1,
      });
    }
  }, []);
  useEffect(() => {
    if (clientUid) {
      changeUid(clientUid);
    }
  }, [clientUid]);
  useEffect(() => {
    if (targetPermissionUserList.length > 0) {
      const settingUsers = permissionList.map((item) => item.targetValue);
      const newPolicy = (targetPermissionUserList.length === 1 &&
      settingUsers.includes(targetPermissionUserList[0])
        ? permissionList.filter(
            (item) => item.targetValue === targetPermissionUserList[0]
          )[0].policy
        : createType.includes("WORKGROUP")
        ? utilsCommon.getValueFromKeyinEcoCommonConfig("ecoCommonConfig")
            .workgroupPermissionDTO.policy
        : utilsCommon.getValueFromKeyinEcoCommonConfig("ecoCommonConfig")
            ?.noneWorkgroupPolicyDTO?.policy) ?? {
        startTime: dayjs().valueOf(),
        allowDays: -1,
        allowDaysTimeZone: serverTimezone,
        allowViewCount: 0,
        currentViewCount: 0,
        allowDownloadCount: 0,
        currentDownloadCount: 0,
        drmPerm: isOCIDefaultSite
          ? []
          : ["view", "print", "print_screen", "extract"],
        webView: true,
        revision: true,
        download: true,
        wrapping: !isOCIDefaultSite,
        additionalViewPermission: "NONE",
      };
      changeTempPolicy({ ...newPolicy, allowDays: newPolicy.allowDays - 1 });
    }
  }, [targetPermissionUserList]);
  useEffect(() => {
    let sessionUser = users;
    if (!sessionUser || !clientUid) return;
    sessionUser = sessionUser.filter(
      (v, i) => sessionUser && sessionUser.indexOf(v) === i
    );
    searchAndAddUser(sessionUser);
  }, [users, clientUid, loginState.loginInfo]);
  useEffect(() => {
    if (currentStep === "CHOOSE_TYPE") {
      changeCopyFileResp({
        ...copyFileResp,
        workgroupId: undefined,
        workgroupName: undefined,
      });
      changeNewWorkgroupInfo(INIT_WORKGROUP_INFO);
    }
  }, [currentStep]);
  useEffect(() => {
    const clientSize = {
      CHOOSE_TYPE: { width: 540, height: 339 }, //워크그룹 선택
      UPLOAD_TO_EXIST_WORKGROUP: { width: 600, height: 725 }, //기존 워크그룹으로 파일 공유 // 718 -> 720 -> 722 -> 723 -> 725
      ADD_NEW_MEMBER: { width: 540, height: 690 }, // 멤버 추가
      CREATE_NEW_WORKGROUP: {
        width: 600,
        height:
          (receivedFiles && receivedFiles?.length > 0 ? 829 : 695) +
          (i18n.language.includes("en") ? 13 : 0), // 773 -> 973 -> 873 -> 833 -> 840 -> 838 -> 837
      }, // 새 워크그룹으로 파일 공유
      CREATE_NEW_SUB_WORKGROUP: {
        width: 600,
        height: receivedFiles && receivedFiles?.length > 0 ? 829 : 695,
      }, // 하위 워크그룹
      SELECT_RECEIVER: {
        width: 600,
        height:
          (receivedFiles && receivedFiles?.length > 0 ? 768 : 690) + extraSize, // 698 => 699 => 720 => 740 => 760 => 770 => 768
      }, // 전송파일 멤버 선택
      SELECT_USER: {
        width: 818,
        height: 714,
      }, // 사용자 선택
      AUTH_SETTING: {
        width: 540,
        height: 721,
      }, // 파일 권한 설정
      CREATE_PRIVATE_WORKGROUP: {
        width: 600,
        height: 760,
      },
    };
    if (currentStep !== "FILE_UPLOAD") {
      modalHeight.current =
        clientSize[
          extraOpenModalInfo === "AUTH_SETTING" ||
          targetPermissionUserList.length > 0
            ? "AUTH_SETTING"
            : extraOpenModalInfo === "SELECT_USER"
            ? extraOpenModalInfo
            : currentStep
        ].height;
      // alert(modalHeight.current);
      utilsClient.setClientHeight(
        Math.min(
          clientSize[
            extraOpenModalInfo === "AUTH_SETTING" ||
            targetPermissionUserList.length > 0
              ? "AUTH_SETTING"
              : extraOpenModalInfo === "SELECT_USER"
              ? extraOpenModalInfo
              : currentStep
          ].height,
          window.screen.height - 80
        )
      );
      utilsClient.setClientWidth(
        clientSize[
          extraOpenModalInfo === "AUTH_SETTING" ||
          targetPermissionUserList.length > 0
            ? "AUTH_SETTING"
            : extraOpenModalInfo === "SELECT_USER"
            ? extraOpenModalInfo
            : currentStep
        ].width
      );
    }
  }, [
    extraSize,
    currentStep,
    receivedFiles,
    extraOpenModalInfo,
    targetPermissionUserList,
  ]);
  useEffect(() => {
    changeFiles(receivedFiles);
  }, [receivedFiles]);

  const searchAndAddUser = async (sessionUser: string[]) => {
    let users = [] as Member[];
    let resp = await Promise.all(
      sessionUser.map(async (user, index) => {
        let answer: any = await getMemberList({
          searchOpt: user,
          // tenancyCode: utilsCommon.getWorkspaceCode() ?? "",
          internalPos: 1,
          externalPos: 1,
        });
        if (answer.payload) {
          if (answer.payload.userList?.length === 0) {
            users.push({
              status: 2,
              userId: user,
              userName: null,
              userType: 2,
              policy: undefined,
            });
          } else {
            // let searchUser = answer.payload.userList[0] as Member;
            let foundSameUser = answer.payload?.userList?.filter(
              (item: Member) =>
                [item.userId, item.userName, item.email]?.includes(user)
            );
            if (foundSameUser && foundSameUser.length >= 1) {
              foundSameUser
                .filter((user1: Member) => user1.userId !== myId)
                .forEach((user: Member) =>
                  users.push({
                    status: user.status,
                    userId: user.userId,
                    userName: user.userName,
                    userType: user.userType,
                    // policy:
                    //   createType === "sendFile"
                    //     ? DEFAULT_POLICY_SETTING
                    //     : undefined,
                  })
                );
            } else {
              users.push({
                status: 2,
                userId: user,
                userName: null,
                userType: 2,
                // policy:
                //   createType === "sendFile"
                //     ? DEFAULT_POLICY_SETTING
                //     : undefined,
              });
            }
          }
          return true;
        }
      })
    );
    if (resp) {
      changeAddedUserList(users);
    }
  };
  const renderChooseTypePage = () => {
    return (
      <Box alignItems="center" justifyContent="center" classes="chooseType">
        <Box
          direction="column"
          onClick={() => changeCurrentStep("UPLOAD_TO_EXIST_WORKGROUP")}
        >
          <Box
            width={158}
            height={84}
            alignItems="center"
            justifyContent="center"
          >
            <img
              src={require("../../shared/images/pages/Workgroup/ExistWorkgroup.png")}
              alt={"thumbnail"}
              width={125}
              height={63}
            />
          </Box>

          <Typography
            style={{ marginTop: "13px" }}
            whiteSpace="normal"
            type="subtitle2"
            textAlign="center"
            width="107px"
          >
            {t("workgroup.shareToExist")}
          </Typography>
        </Box>
        <Box
          direction="column"
          onClick={() => changeCurrentStep("CREATE_NEW_WORKGROUP")}
        >
          <Box
            width={158}
            height={84}
            alignItems="center"
            justifyContent="center"
          >
            <img
              src={require("../../shared/images/pages/Workgroup/newWorkgroup.png")}
              alt={"thumbnail"}
              width={154}
              height={82}
            />
          </Box>

          <Typography
            style={{ marginTop: "13px" }}
            whiteSpace="normal"
            type="subtitle2"
            textAlign="center"
            width="100px"
          >
            {t("workgroup.shareToNew")}
          </Typography>
        </Box>
      </Box>
    );
  };
  const handleCreatePrivateWorkgroup = async (newWorkgroupId: string) => {
    changeSuccessAddPrivate([]);
    for (let i = 0; i < addedUserList.length; i++) {
      let resp = await createNewPrivateWorkgroups({
        workgroup: {
          ...newWorkgroupInfo,
          parentWorkgroupId: newWorkgroupId,
          managerId: loginDto?.userInfo.userId ?? "",
          members: [
            addedUserList[i],
            {
              userId: loginDto?.userInfo.userId,
              status: loginDto?.ecoUserInfo.status,
              userType: loginDto?.ecoUserInfo.userType,
            },
          ],
        },
        policy: {
          ...utilsCommon.getValueFromKeyinEcoCommonConfig("ecoCommonConfig")
            .policy,
          authType: "LOGIN",
          changeCode: "CREATE",
          createUser: "",
          policyCode: null,
          policyType: "TEMPLATE",
          syncId: "NONE",
          targetType: "WORKGROUP",
          uniqueId: null,
          updateTime: 1,
          updateUser: "",
          policy: {
            ...policy,
            allowDays:
              policy.allowDays === -1 ? -1 : (policy.allowDays ?? 0) + 1,
          },
        },
      });
      if (resp.payload) {
        changeSuccessAddPrivate((successAddPrivate) => [
          ...successAddPrivate,
          addedUserList[i],
        ]);
      }
    }
    if (files) return;
    if (
      !(
        utilsCommon.isClientLogin("MAC") || utilsCommon.isClientLogin("WINDOWS")
      )
    ) {
      navigate(
        utilsCommon.renderProperNavigatePath(`/workgroup/${newWorkgroupId}/log`)
      );
      getWorkgroupSideList(workgroupState.savedSidemenuFilter);
      toasts.toastMsg(nanoid(), t("workgroup.alert.createNew"), "success");
      removeUid();
      onClose();
    } else {
      navigate(
        utilsCommon.renderProperNavigatePath(
          `/workgroup/${newWorkgroupId}/home`
        )
      );
      onClose();
    }
  };
  const [successAddPrivate, changeSuccessAddPrivate] = useState<
    {
      email?: string | null | undefined;
      status: number;
      userId: string;
      userName?: string | null | undefined;
      userType: number;
    }[]
  >([]);
  const renderCreateSubWorkgroups = () => {
    return (
      <Box
        direction="column"
        height={684}
        width={440}
        style={{ paddingTop: 200, boxSizing: "border-box" }}
      >
        <Box justifyContent="space-between">
          <Typography>{t("workgroup.privateWorkgroupCreateTitle")}</Typography>
          <Typography exactColor={getColor("COLOR_TEXT_BRAND")}>
            {`(${successAddPrivate.length}/${addedUserList.length})`}
          </Typography>
        </Box>
        <progress
          id="progress"
          value={successAddPrivate.length}
          max={addedUserList.length}
          className="mg mt-12"
        />
        <Typography
          type="caption1_rg"
          classes="mg mt-20"
          textAlign="center"
          exactColor={getColor("COLOR_TEXT_SECONDARY")}
        >
          {t("workgroup.privateWorkgroupCreateDesc")}
        </Typography>
      </Box>
    );
  };
  const renderBodyByType = () => {
    switch (currentStep) {
      case "CHOOSE_TYPE":
        return renderChooseTypePage();
      case "CREATE_PRIVATE_WORKGROUP":
        return renderCreateSubWorkgroups();
      case "CREATE_NEW_WORKGROUP":
      case "CREATE_NEW_SUB_WORKGROUP":
      case "ADD_NEW_MEMBER":
        return (
          <CreateNewWorkgroupPage
            files={files}
            uid={uid}
            copyFileResp={copyFileResp}
            workgroupInfo={newWorkgroupInfo}
            changeExtraOpenModalInfo={changeExtraOpenModalInfo}
            disableValues={disableValues ?? [myId]}
            addedUserList={addedUserList}
            changeAddedUserList={changeAddedUserList}
            changeIsNextButtonDisabled={changeIsNextButtonDisabled}
            changeWorkgroupInfo={changeNewWorkgroupInfo}
            modalType={currentStep}
            message={message}
            createType={createType}
            isClient={targetType === "client"}
            changeCopyFileResp={changeCopyFileResp}
            changeMessage={changeMessage}
            changeFiles={changeFiles}
            changeExtraSize={changeExtraSize}
            clientSize={
              targetType === "client"
                ? {
                    height:
                      receivedFiles && receivedFiles?.length > 0 ? 780 : 690,
                    width: 600,
                  }
                : undefined
            }
            isSendShareMail={isSendShareMail}
            changeSendShareMail={changeSendShareMail}
            myBoxFileShareLinkPolicy={myBoxFileShareLinkPolicy}
            changeMyBoxFileShareLinkPolicy={changeMyBoxFileShareLinkPolicy}
          />
        );
      case "UPLOAD_TO_EXIST_WORKGROUP":
        return (
          <ExistWorkgroupPage
            files={files}
            uid={uid}
            changeFiles={changeFiles}
            targetType={targetType}
            copyFileResp={copyFileResp}
            changeCopyFileResp={changeCopyFileResp}
            changeIsNextButtonDisabled={changeIsNextButtonDisabled}
          />
        );
      case "SELECT_RECEIVER":
        return (
          <CreateNewWorkgroupPage
            files={files}
            createType={createType}
            changeFiles={changeFiles}
            uid={uid}
            disableValues={disableValues ?? [myId]}
            addedUserList={addedUserList}
            permissionList={permissionList}
            changeAddedUserList={changeAddedUserList}
            changeIsNextButtonDisabled={changeIsNextButtonDisabled}
            changePermissionList={changePermissionList}
            modalType={currentStep}
            changeTargetPermissionUserList={changeTargetPermissionUserList}
            message={message}
            changeMessage={changeMessage}
            isClient={targetType === "client"}
            changeExtraSize={changeExtraSize}
            clientSize={
              targetType === "client"
                ? {
                    height:
                      receivedFiles && receivedFiles?.length > 0 ? 698 : 690,
                    width: 600,
                  }
                : undefined
            }
            changeExtraOpenModalInfo={changeExtraOpenModalInfo}
            isSendShareMail={isSendShareMail}
            changeSendShareMail={changeSendShareMail}
            myBoxFileShareLinkPolicy={myBoxFileShareLinkPolicy}
            changeMyBoxFileShareLinkPolicy={changeMyBoxFileShareLinkPolicy}
          />
        );
    }
    return <Box>{currentStep}</Box>;
  };
  const renderModalTitle = () => {
    switch (currentStep) {
      case "CHOOSE_TYPE":
        return t("workgroup.chooseWorkgroup");
      case "CREATE_NEW_WORKGROUP":
      case "CREATE_PRIVATE_WORKGROUP":
        return files ? t("workgroup.shareToNew") : t("workgroup.create");
      case "CREATE_NEW_SUB_WORKGROUP":
        return t("workgroup.createSub");
      case "UPLOAD_TO_EXIST_WORKGROUP":
        return t("workgroup.shareToExist");
      case "ADD_NEW_MEMBER":
        return t("workgroup.addMember");
      case "SELECT_RECEIVER":
        return createType === "ADD_RECIPIENTS_SEND_FILE"
          ? t("sendingFile.addUser")
          : targetType === "client"
          ? t("sendingFile.clientTitle")
          : t("sendingFile.title");
    }
    return t("workgroup.chooseWorkgroup");
  };
  const handleClosemodal = () => {
    // 파일이 있는 경우에 해당 모달 오픈하도록
    if (targetType === "client") utilsClient.closeClient();
    else if (uploadedFiles || receivedFiles || files)
      changeExtraOpenModalInfo("CONFIRM_STOP_SHARING");
    else {
      onClose();
      removeUid();
    }
  };

  /** 파일 전송하기 버튼 클릭시 함수 */
  const handleSendFile = async () => {
    if (!uid) return;
    let resp = await doDocUploadFileCompleteDelivery(uid, {
      folderId: "",
      memo: "",
      message: message ?? "",
      workgroupId: "NONEWORKGROUP",
      sendShareMail: isSendShareMail,
      permissionList: addedUserList.map((user) => {
        return {
          authType: "LOGIN",
          changeCode: "CREATE",
          createUser: myId,
          policyCode: null,
          policyType: "DOCUMENT",
          syncId: "",
          targetType: "USER",
          targetValue: user.userId,
          uniqueId: null,
          updateTime: 1,
          updateUser: myId,
          workgroupCode: "NONEWORKGROUUP",
          policy: {
            ...(permissionList.filter(
              (item) => item.targetValue === user.userId
            )[0]?.policy ??
              (createType.includes("WORKGROUP")
                ? utilsCommon.getValueFromKeyinEcoCommonConfig(
                    "ecoCommonConfig"
                  ).workgroupPermissionDTO.policy
                : utilsCommon.getValueFromKeyinEcoCommonConfig(
                    "ecoCommonConfig"
                  )?.noneWorkgroupPolicyDTO?.policy) ?? {
                startTime: dayjs().valueOf(),
                allowDays: -1,
                allowDaysTimeZone: serverTimezone,
                allowViewCount: 0,
                currentViewCount: 0,
                allowDownloadCount: 0,
                currentDownloadCount: 0,
                drmPerm: isOCIDefaultSite
                  ? []
                  : ["view", "print", "print_screen", "extract"],
                webView: true,
                revision: true,
                download: true,
                wrapping: !isOCIDefaultSite,
                additionalViewPermission: "NONE",
              }),
            fileShareLinkPolicy: {
              ...myBoxFileShareLinkPolicy,
              password: "",
              startTime: dayjs().valueOf(),
              allowDays:
                myBoxFileShareLinkPolicy.allowDays === -1
                  ? -1
                  : myBoxFileShareLinkPolicy.allowDays + 1,
              allowDaysTimeZone: serverTimezone,
            },
          },
        };
      }),
    });
    if (resp.payload && !resp.payload.error) {
      if (targetType === "client") {
        utilsClient.uploadComplete({
          workgroupId: "NONEWORKGROUP",
          uploadedDocs: resp.payload?.map((item: any) => {
            return {
              syncId: item.syncId,
              fileId: item.fileId,
              downloadFileType: item?.downloadFileType ?? "ECO",
            };
          }),
        });
      } else {
        toasts.toastMsg(
          nanoid(),
          t("sendingFile.alert.sendSuccess"),
          "success"
        );
        utilsCommon.renderProperNavigatePath("/myInbox/sent");

        onClose();
        onSubmitAfter && onSubmitAfter();
        removeUid();
      }
    } else {
      if (
        utilsCommon.isClientLogin("MAC") ||
        utilsCommon.isClientLogin("WINDOWS")
      ) {
        utilsClient.reportErrorToClient(resp?.payload?.error);
      } else {
        toasts.toastMsg(
          nanoid(),
          resp.payload?.error?.message ?? t("sendingFile.alert.sendFail"),
          "error"
        );
        utilsClient.closeClient();
        onClose();
        removeUid();
      }
    }
  };
  const handleCopyFiles = async () => {
    let workgroupId = copyFileResp.workgroupId ?? "";
    if (!copyFileResp.files) return;
    if (utilsCommon.isDemoEco() && copyFileResp.files) {
      showDemoDownloadModal();
      return;
    }
    let tempUid = uid ?? documentState.uid;
    if (targetType !== "DROPBOX" && !tempUid) {
      const uidResp: any = await getDocUploadUidCreate();
      await getDocDeliveryUid({
        syncIds: copyFileResp.files.map((item) => item.syncId),
        uniqueId: uidResp.payload,
      });
      tempUid = uidResp.payload;
    }
    let createdWorkgroupInfo = {} as Workgroup;
    if (currentStep === "CREATE_NEW_WORKGROUP" && workgroupId === "") {
      createdWorkgroupInfo = await handleCreateNewWorkgroup();
      workgroupId = createdWorkgroupInfo?.workgroupId ?? "";
      if (!workgroupId || workgroupId === "") {
        return;
      }
    }
    const newSideListPayload: any = await getWorkgroupSideList(
      workgroupState.savedSidemenuFilter
    );
    if (!newSideListPayload.payload) return;
    const newSideList = newSideListPayload.payload?.workgroupList;
    const targetWorkgroup =
      newSideList.filter(
        (item: Workgroup) => item.workgroupId === workgroupId
      )[0] ?? createdWorkgroupInfo;
    const subWorkgroup = newSideList.filter(
      (item: Workgroup) => item.parentWorkgroupId === workgroupId
    );
    let resp2: any = await doDocUploadFileComplete(
      tempUid,
      {
        workgroupId: workgroupId,
        message: copyFileResp.message,
        targetWorkgroupIdList:
          targetWorkgroup.workgroupType === "GENERAL"
            ? []
            : targetWorkgroup.workgroupType === "PRIVATE"
            ? [targetWorkgroup.workgroupId]
            : subWorkgroup.map((item: Workgroup) => item.workgroupId),
        permissionList: [{ changeCode: "CREATE", targetType: "WORKGROUP" }],
        isMultiple: targetWorkgroup.workgroupType !== "GENERAL",
      },
      true
    );
    if (resp2.payload) {
      if (
        utilsCommon.isClientLogin("MAC") ||
        utilsCommon.isClientLogin("WINDOWS")
      ) {
        utilsClient.uploadComplete({
          workgroupId: workgroupId,
          workgroupType: targetWorkgroup.workgroupType,
          workgroupName:
            newWorkgroupInfo.workgroupName &&
            newWorkgroupInfo.workgroupName !== ""
              ? newWorkgroupInfo.workgroupName
              : copyFileResp.workgroupName ?? "",
          uploadedDocs: resp2.payload.map((item: any) => {
            return {
              syncId: item.syncId,
              fileId: item.fileId,
              downloadFileType: item?.downloadFileType ?? "ECO",
            };
          }),
        });
      } else {
        setUpdateUploadProgressFiles({} as UploadFile);
        toasts.toastMsg(nanoid(), t("file.alert.uploadComplete"), "success");
        navigate(
          utilsCommon.renderProperNavigatePath(`/workgroup/${workgroupId}/file`)
        );
        onClose();
        removeUid();
      }
    } else {
      setUpdateUploadProgressFiles({} as UploadFile);
      if (
        utilsCommon.isClientLogin("MAC") ||
        utilsCommon.isClientLogin("WINDOWS")
      ) {
        utilsClient.reportErrorToClient(resp2?.error);
      } else {
        toasts.toastMsg(
          nanoid(),
          resp2?.error?.message ?? t("file.alert.uploadFail"),
          "error"
        );
        onClose();
        utilsClient.closeClient();
      }
    }
  };
  const handleAddNewMember = async () => {
    if (!workgroupInfo) return;
    let resp: any = await updateWorkgroupInfo({
      workgroupId: workgroupInfo?.workgroupId,
      workgroup: {
        ...workgroupInfo,
        invitedMessage: message ?? "",
        members: [
          ...(workgroupInfo.members ?? []),
          ...addedUserList.map((user) => {
            return {
              ...user,
              workgroupId: "",
              workgroupName: "",
              phoneNumber: "",
              companyCode: "",
              companyName: "",
              deptCode: "",
              positionCode: "",
              positionName: "",
              roleCode: "",
              roleName: "",
              etc: "",
              linkedYN: "",
              withdrawYN: "",
              workgroupCount: "",
              lastLoginTime: 0,
              inviteTime: 0,
              deptName: "",
              userName: user.userName ?? "",
              email: user.email ?? "",
              changeCode: "CREATE",
            };
          }),
        ],
      },
    });
    if (resp.payload) {
      await getWorkgroupDetail({
        workgroupId: workgroupInfo?.workgroupId,
      });
      if (workgroupInfo.workgroupType === "HUB") {
        getWorkgroupSideList(workgroupState.savedSidemenuFilter);
      }
      toasts.toastMsg(nanoid(), t("common.alert.addSuccess"), "success");
    } else {
      toasts.toastMsg(
        nanoid(),
        resp.error.message ?? t("common.alert.fail"),
        "error"
      );
    }
    if (
      !(
        utilsCommon.isClientLogin("MAC") || utilsCommon.isClientLogin("WINDOWS")
      )
    ) {
      removeUid();
      onClose();
    }
  };
  const handleCreateNewWorkgroup = async () => {
    const resp: any = await createNewWorkgroup({
      workgroup: {
        ...newWorkgroupInfo,
        parentWorkgroupId: parentWorkgroupId,
        managerId: loginDto?.userInfo.userId ?? "",
        members: [
          ...addedUserList,
          {
            userId: loginDto?.userInfo.userId,
            status: loginDto?.ecoUserInfo.status,
            userType: loginDto?.ecoUserInfo.userType,
          },
        ],
      },
      policy: {
        ...utilsCommon.getValueFromKeyinEcoCommonConfig("ecoCommonConfig")
          .policy,
        authType: "LOGIN",
        changeCode: "CREATE",
        createUser: "",
        policyCode: null,
        policyType: "TEMPLATE",
        syncId: "NONE",
        targetType: "WORKGROUP",
        uniqueId: null,
        updateTime: 1,
        updateUser: "",
        policy: {
          ...policy,
          allowDays: policy.allowDays === -1 ? -1 : (policy.allowDays ?? 0) + 1,
        },
      },
    });
    if (resp.payload) {
      if (newWorkgroupInfo.createWorkgroupHub) {
        changeCurrentStep("CREATE_PRIVATE_WORKGROUP");
        await handleCreatePrivateWorkgroup(resp?.payload?.workgroupId);
        return resp.payload;
      } else if (files) {
        return resp.payload;
      } else {
        if (
          !(
            utilsCommon.isClientLogin("MAC") ||
            utilsCommon.isClientLogin("WINDOWS")
          )
        ) {
          navigate(
            utilsCommon.renderProperNavigatePath(
              `/workgroup/${resp.payload.workgroupId}/${
                files ? "file" : "home"
              }`
            )
          );
          toasts.toastMsg(nanoid(), t("workgroup.alert.createNew"), "success");
          removeUid();
          onClose();
        }
        return resp.payload;
      }
    } else {
      toasts.toastMsg(nanoid(), resp.error.message, "error");
      return "";
    }
  };
  const handleAddRecieptUser = () => {
    onSubmitAfter &&
      onSubmitAfter(
        addedUserList.map((item) => {
          return {
            ...item,
            policy:
              permissionList.filter(
                (item1) => item1.targetValue === item.userId
              )[0]?.policy ??
              (createType.includes("WORKGROUP")
                ? utilsCommon.getValueFromKeyinEcoCommonConfig(
                    "ecoCommonConfig"
                  ).workgroupPermissionDTO.policy
                : utilsCommon.getValueFromKeyinEcoCommonConfig(
                    "ecoCommonConfig"
                  )?.noneWorkgroupPolicyDTO?.policy) ??
              customPolicy,
          };
        }),
        {
          sendShareMail: isSendShareMail,
          message: message,
        }
      );
    removeUid();
    onClose();
  };
  const handleApplyPolicy = (policy: PermissionPolicy) => {
    changeExtraOpenModalInfo("");
    if (targetPermissionUserList.length > 0) {
      const customUserList = permissionList.map((item) => item.targetValue);
      let tempPermissionList = permissionList.map((item) =>
        targetPermissionUserList.includes(item.targetValue)
          ? {
              ...item,
              policy: {
                ...policy,
                allowDays:
                  policy.allowDays !== undefined && policy.allowDays >= 0
                    ? policy.allowDays + 1
                    : -1,
              },
            }
          : item
      );
      tempPermissionList = tempPermissionList.concat(
        targetPermissionUserList
          .filter((user) => !customUserList.includes(user))
          .map((user) => {
            return {
              targetValue: user,
              policy: {
                ...policy,
                allowDays:
                  policy.allowDays !== undefined && policy.allowDays >= 0
                    ? policy.allowDays + 1
                    : -1,
              },
            };
          })
      );
      changePermissionList(tempPermissionList);
      changeTargetPermissionUserList([]);
    } else {
      changePolicy(policy);
    }
  };
  return extraOpenModalInfo === "CONFIRM_STOP_SHARING" ? (
    <Modal
      width={540}
      open={open}
      onClose={() => changeExtraOpenModalInfo("")}
      title={t("file.stopSharing")}
      bodyMsg={t("file.stopSharingDesc")}
      footer={
        <Box>
          <Button
            btnStyle="fill"
            label={t("common.cancel")}
            type={"secondary"}
            onClick={() => changeExtraOpenModalInfo("")}
          />
          <Button
            btnStyle="fill"
            label={t("file.stopSharing")}
            onClick={() => {
              changeExtraOpenModalInfo("");
              removeUid();
              onClose();
            }}
          />
        </Box>
      }
    />
  ) : currentStep === "FILE_UPLOAD" ? (
    <FileUploadModal
      open
      onClose={onClose}
      type={targetType === "DROPBOX" ? "DROPBOX" : "default"}
      modalType={
        createType === "SHARE_TO_WORKGROUP" ? "SHARE_TO_WORKGROUP" : undefined
      }
      onNext={(newFile, uid) => {
        changeCurrentStep(
          targetType === "DROPBOX" || createType === "SHARE_TO_WORKGROUP"
            ? "CHOOSE_TYPE"
            : "SELECT_RECEIVER"
        );
        changeFiles(newFile);
        changeUid(uid);
      }}
    />
  ) : extraOpenModalInfo === "SELECT_USER" ? (
    <SelectUserModal
      open={extraOpenModalInfo === "SELECT_USER"}
      isClient={targetType === "client"}
      addedUserList={addedUserList}
      disableValues={disableValues}
      onClick={(users) => changeAddedUserList(users)}
      onClose={() => changeExtraOpenModalInfo("")}
    />
  ) : extraOpenModalInfo === "AUTH_SETTING" ||
    targetPermissionUserList.length > 0 ? (
    <Modal
      width={540}
      bodyHeight={563}
      open={open}
      isExit={targetType !== "client"}
      onClose={() => {
        changeTempPolicy(policy);
        changeExtraOpenModalInfo("");
        changeTargetPermissionUserList([]);
      }}
      title={
        targetPermissionUserList.length > 1 ? (
          t("sendingFile.setAuth.changeAllTitle")
        ) : targetPermissionUserList.length === 1 ? (
          <Box alignItems="center">
            ‘
            <Tooltip
              message={
                addedUserList.filter(
                  (item) => item.userId === targetPermissionUserList[0]
                )[0]?.userName ?? targetPermissionUserList[0]
              }
            >
              <Typography ellipsis maxWidth={272} type="h2">
                {addedUserList.filter(
                  (item) => item.userId === targetPermissionUserList[0]
                )[0]?.userName ?? targetPermissionUserList[0]}
              </Typography>
            </Tooltip>
            {"’ " + t("common.setAuth")}
          </Box>
        ) : (
          t("workgroup.workgroupAuthTitle")
        )
      }
      classes="authModal"
      isBack
      onBack={() => {
        changeTempPolicy(policy);
        changeExtraOpenModalInfo("");
        changeTargetPermissionUserList([]);
      }}
      footer={
        <Box>
          <Button
            btnStyle="fill"
            label={t("common.cancel")}
            type={"secondary"}
            onClick={() => {
              changeTempPolicy(policy);
              changeExtraOpenModalInfo("");
              changeTargetPermissionUserList([]);
            }}
          />
          <Button
            btnStyle="fill"
            label={t("common.apply")}
            onClick={() => handleApplyPolicy(tempPolicy)}
          />
        </Box>
      }
      body={
        <SetPolicyPage
          policy={tempPolicy}
          changePolicy={changeTempPolicy}
          page={createType.includes("SEND_FILE") ? "SEND_FILE" : "WORKGROUP"}
          isInd={createType.includes("SEND_FILE")}
          isClient={targetType === "client"}
          isNew
        />
      }
    />
  ) : (
    <Modal
      width={currentStep === "CHOOSE_TYPE" ? 540 : 600}
      open={open}
      isExit={
        targetType !== "client" && currentStep !== "CREATE_PRIVATE_WORKGROUP"
      }
      onClose={() => {
        utilsClient.closeClient();
        removeUid();
        onClose();
      }}
      title={renderModalTitle()}
      classes="workgroupAddModal"
      isBack={
        (currentStep !== "CHOOSE_TYPE" &&
          currentStep !== "CREATE_PRIVATE_WORKGROUP" &&
          createType === "SHARE_TO_WORKGROUP") ||
        createType === "ADD_RECIPIENTS_SEND_FILE"
      }
      onBack={() => {
        if (createType !== "ADD_RECIPIENTS_SEND_FILE") {
          changeCurrentStep("CHOOSE_TYPE");
        } else {
          removeUid();
          onClose();
        }
      }}
      isFooter={
        !["CHOOSE_TYPE", "CREATE_PRIVATE_WORKGROUP"].includes(currentStep)
      }
      footer={
        <Box width={"100%"} alignItems="center" justifyContent="flex-end">
          {![
            "ADD_NEW_MEMBER",
            "UPLOAD_TO_EXIST_WORKGROUP",
            "SELECT_RECEIVER",
          ].includes(currentStep) && (
            <Button
              btnStyle="link"
              size="lg"
              leadingIcon={<Icons variant="line" label="shield" isOn={true} />}
              label={t("workgroup.workgroupSetAuth")}
              onClick={() => changeExtraOpenModalInfo("AUTH_SETTING")}
              classes="mg ml-0 mr-auto workgroupAuth"
            />
          )}
          <Button
            btnStyle="fill"
            label={t("common.cancel")}
            classes="mg ml-auto"
            type={"secondary"}
            onClick={() =>
              currentStep === "ADD_NEW_MEMBER" ? onClose() : handleClosemodal()
            }
          />
          <Button
            btnStyle="fill"
            label={
              currentStep === "SELECT_RECEIVER"
                ? loginDto?.ecoSystemLicenseStats?.featureAccessType === "LITE"
                  ? t("common.send")
                  : createType === "ADD_RECIPIENTS_SEND_FILE"
                  ? t("common.apply")
                  : targetType === "client"
                  ? t("common.create")
                  : t("common.send")
                : files || uploadedFiles
                ? t("common.share")
                : currentStep === "ADD_NEW_MEMBER"
                ? t("common.add")
                : t("common.create")
            }
            state={isNextButtonDisabled ? "disable" : "default"}
            onClick={() => {
              if (files || uploadedFiles) {
                const requiredCheck = [
                  ...(files ?? []),
                  ...(uploadedFiles ?? []),
                ].filter(
                  (item) =>
                    !utilsCommon.checkExtensionAvailable(
                      "fileName" in item ? item.fileName : item.contentName,
                      "fwocExtensions"
                    )
                );

                if (requiredCheck?.length > 0) {
                  changeExtraOpenModalInfo("CONFIRM_NO_FWOC_FILES");
                  return;
                }
              }
              currentStep === "ADD_NEW_MEMBER"
                ? handleAddNewMember()
                : files || uploadedFiles
                ? currentStep === "SELECT_RECEIVER"
                  ? handleSendFile()
                  : handleCopyFiles()
                : currentStep === "SELECT_RECEIVER"
                ? handleAddRecieptUser()
                : handleCreateNewWorkgroup();
            }}
          />
        </Box>
      }
      bodyPadding={"8px 24px 0px 28px"}
      body={
        <Box width={"100%"} height="100%" justifyContent="center">
          {extraOpenModalInfo === "CONFIRM_NO_FWOC_FILES" && (
            <Modal
              width={540}
              open={extraOpenModalInfo === "CONFIRM_NO_FWOC_FILES"}
              onClose={() => changeExtraOpenModalInfo("CLOSE")}
              title={t("file.previewNoSupportFile")}
              bodyMsg={t("file.previewNoSupportFileDesc")}
              footer={
                <Box>
                  <Button
                    label={t("common.cancel")}
                    type="secondary"
                    btnStyle="fill"
                    onClick={() => changeExtraOpenModalInfo("CLOSE")}
                  />
                  <Button
                    label={t("common.send")}
                    btnStyle="fill"
                    onClick={() => {
                      changeExtraOpenModalInfo("CLOSE");
                      currentStep === "ADD_NEW_MEMBER"
                        ? handleAddNewMember()
                        : files || uploadedFiles
                        ? currentStep === "SELECT_RECEIVER"
                          ? handleSendFile()
                          : handleCopyFiles()
                        : currentStep === "SELECT_RECEIVER"
                        ? handleAddRecieptUser()
                        : handleCreateNewWorkgroup();
                    }}
                  />
                </Box>
              }
            />
          )}
          {renderBodyByType()}
        </Box>
      }
    />
  );
};
export default WorkgroupAddModal;
